import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, ArrowUpCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AsyncListShell } from "@/components/admin/AsyncListShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ESTADOS = ["Nuevo", "Contactado", "Aprobado", "Rechazado"];
const CATEGORIAS = ["Financieras y bancos", "Mecánicos", "Hojalatería y pintura", "Agencias de autos nuevos", "Otro"];
const ESTADO_COLORS = {
  "Nuevo": "bg-[#C4674F]/15 text-[#A0553F]",
  "Contactado": "bg-[#F1F3F3] text-[#3E5641]",
  "Aprobado": "bg-[#A0553F] text-white",
  "Rechazado": "bg-red-50 text-red-700",
};

export default function PartnerApplicationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/partner-applications");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((it) => {
    if (estadoFilter !== "all" && it.estado !== estadoFilter) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return [it.nombre_negocio, it.nombre_contacto, it.correo, it.telefono, it.categoria]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(t));
  }), [items, q, estadoFilter]);

  async function onDelete(id) {
    if (!window.confirm("¿Eliminar esta solicitud?")) return;
    try { await api.delete(`/partner-applications/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  async function onPromote(item) {
    if (!window.confirm(
      `¿Promover "${item.nombre_negocio}" a Socio comercial?\n\nSe creará un socio nuevo (oculto en el home hasta que actives "visible"). La solicitud quedará marcada como Aprobada.`
    )) return;
    try {
      await api.post(`/partner-applications/${item.id}/promote`);
      toast.success("Socio creado. Ve a 'Socios comerciales' para subir su logo y hacerlo visible.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="admin-partner-applications">
      <header className="mb-8">
        <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Módulo 05</p>
        <h1 className="mt-1 font-serif text-3xl text-[#3E5641] md:text-4xl">Solicitudes de socios</h1>
        <p className="mt-1 text-sm text-[#6B7678]">
          Negocios que se postularon como socios comerciales. La aprobación se hace manualmente aquí — solo entonces
          se genera un registro en el módulo "Socios comerciales".
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input placeholder="Buscar por negocio, contacto, correo…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="pa-search" />
        <Select value={estadoFilter} onValueChange={setEstadoFilter}>
          <SelectTrigger className="w-56" data-testid="pa-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <AsyncListShell
        loading={loading}
        isEmpty={filtered.length === 0}
        loadingTestId="pa-loading"
        emptyTestId="pa-empty"
        emptyLabel="Sin solicitudes aún."
      >
        <div className="overflow-hidden rounded-sm border border-[#3E5641]/10 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3F3] text-left text-xs uppercase tracking-wider text-[#6B7678]">
                <tr>
                  <th className="px-4 py-3">Negocio</th>
                  <th className="px-4 py-3">Categoría</th>
                  <th className="px-4 py-3">Contacto</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3E5641]/10">
                {filtered.map((it) => (
                  <tr key={it.id} className="hover:bg-[#F1F3F3]/50" data-testid={`pa-row-${it.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#3E5641]">{it.nombre_negocio}</div>
                      <div className="text-xs text-[#6B7678]">{it.descripcion}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{it.categoria}</td>
                    <td className="px-4 py-3">
                      <div>{it.nombre_contacto}</div>
                      <div className="text-xs text-[#6B7678]">{it.telefono} · {it.correo}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-sm px-2 py-1 text-xs ${ESTADO_COLORS[it.estado] || ""}`}>{it.estado}</span>
                      {it.promoted_to_partner_id && <div className="mt-1 mono text-[10px] text-[#A0553F]">Promovido</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!it.promoted_to_partner_id && (
                        <button onClick={() => onPromote(it)} className="mr-2 rounded-sm p-1 text-[#A0553F] hover:bg-[#C4674F]/10" aria-label="Promover" data-testid={`pa-promote-${it.id}`}><ArrowUpCircle size={14} /></button>
                      )}
                      <button onClick={() => setEditing(it)} className="mr-2 rounded-sm p-1 text-[#3E5641] hover:bg-[#F1F3F3]" data-testid={`pa-edit-${it.id}`}><Pencil size={14} /></button>
                      <button onClick={() => onDelete(it.id)} className="rounded-sm p-1 text-red-600 hover:bg-red-50" data-testid={`pa-delete-${it.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AsyncListShell>

      {editing && <PartnerAppDialog key={editing.id} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PartnerAppDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre_negocio: item.nombre_negocio, categoria: item.categoria,
    nombre_contacto: item.nombre_contacto, telefono: item.telefono, correo: item.correo,
    descripcion: item.descripcion, estado: item.estado, notas: item.notas || "",
  });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/partner-applications/${item.id}`, {
        ...form,
        notas: form.notas?.trim() || null,
      });
      toast.success("Solicitud actualizada");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{item.nombre_negocio}</DialogTitle></DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <F label="Negocio"><Input value={form.nombre_negocio} onChange={(e) => setF("nombre_negocio", e.target.value)} /></F>
          <div>
            <Label className="field-label">Categoría</Label>
            <Select value={form.categoria} onValueChange={(v) => setF("categoria", v)}>
              <SelectTrigger data-testid="pa-cat"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <F label="Contacto"><Input value={form.nombre_contacto} onChange={(e) => setF("nombre_contacto", e.target.value)} /></F>
          <F label="Teléfono"><Input value={form.telefono} onChange={(e) => setF("telefono", e.target.value)} /></F>
          <F label="Correo"><Input type="email" value={form.correo} onChange={(e) => setF("correo", e.target.value)} /></F>
          <div>
            <Label className="field-label">Estado</Label>
            <Select value={form.estado} onValueChange={(v) => setF("estado", v)}>
              <SelectTrigger data-testid="pa-estado"><SelectValue /></SelectTrigger>
              <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="field-label">Descripción</Label>
          <Textarea rows={3} value={form.descripcion} onChange={(e) => setF("descripcion", e.target.value)} />
        </div>
        <div>
          <Label className="field-label">Notas internas</Label>
          <Textarea rows={3} value={form.notas} onChange={(e) => setF("notas", e.target.value)} data-testid="pa-notas" />
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="pa-save">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) { return <div><Label className="field-label">{label}</Label>{children}</div>; }
